// 📦 Imports
import { useEffect, useState, useCallback } from "react";
import Header from "./components/layout/Header";
import ResultScreen from "./components/layout/ResultScreen";
import EquationBox from "./components/layout/EquationBox";
import SideBar from "./components/layout/SideBar";
import { Helmet } from "react-helmet";
import api from "../../../../api";

// 🎮 EquationGame Component
export default function EquationGame() {
  // 🔧 State
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasOutreached, setHasOutreached] = useState(false);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isCleared, setIsCleared] = useState(false);
  const [isClearedOnce, setIsClearedOnce] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [levelProgress, setLevelProgress] = useState([]);
  const [shouldEndGame, setShouldEndGame] = useState(false); // ⏳ Track when we should end the game
  const [hasLoggedGame, setHasLoggedGame] = useState(false);

  // Fetch profile data
  useEffect(() => {
    api.get("user/get_level_progress/")
      .then((res) => {
        console.log("Level progress:", res.data);
        setLevelProgress(res.data);
      })
      .catch((err) => {
        console.error("Error fetching level progress:", err);
      });
  }, []);

  // ✅ End game only after levelProgress has updated
  useEffect(() => {
    if (shouldEndGame) {
      setGameOver(true);
      setShouldEndGame(false); // 🔄 Reset flag
    }
  }, [shouldEndGame]);

  // 🚀 Fetch a new question from backend
  const getNewQuestion = useCallback(async () => {
    const res = await fetch(
      `https://matly-backend.onrender.com/api/ligningsmester/level1/generate/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        }
      }
    );
    const data = await res.json();
    console.log("Fetching new question...", data);
    setQuestion(data);
    setFeedback("");
    setAnswer("");
    setStartTime(Date.now()); // Save time in milliseconds
  }, []);

  // 📄 Submit answer to backend
  const submitAnswer = async () => {
    const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000); // in seconds
        setElapsedSeconds(elapsedSeconds);
    
    const res = await fetch("https://matly-backend.onrender.com/api/ligningsmester/level1/check_answer/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
      body: JSON.stringify({
        question: question.question,
        user_answer: parseInt(answer),
        time_taken: elapsedSeconds,
      }),
    });
    const data = await res.json();
    setScore((prevScore) => prevScore + data.points_awarded);
    setFeedback(data);
    console.log("score", score);
    console.log("data", data.elapsed);
  };

  // 🎬 Start new game session
  const startGame = useCallback(() => {
    setQuestionNumber(1);
    setScore(0);
    setFeedback(null);
    setHasSubmitted(false);
    setGameOver(false);
    setIsCleared(false);
    setIsClearedOnce(false);
    setHasLoggedGame(false);
    api.get("user/get_level_progress/")
      .then((res) => {
        console.log("Level progress refreshed on restart:", res.data);
        setLevelProgress(res.data);
      })
      .catch((err) => {
        console.error("Error refreshing level progress:", err);
      });
    setTimeout(() => {
      getNewQuestion();
    }, 0);
  }, [getNewQuestion]);

  // 🧾 Log gameprogression, XP and badge at game end
  const logGame = async () => {
    if (hasLoggedGame) return; // Prevent multiple logs
    setHasLoggedGame(true);

    if (isCleared) {
      await api.post("user/track_level_progress/", {
        game: "ligningsmester",
        level: 1,
      });
    }
    if (score > 20) {
      console.log("Calling sendXp with:", 95);
      sendXp(95);
    }
  };
  
  // 📥 Send XP to backend
  const sendXp = async (amount) => {
    const token = localStorage.getItem("access");
    console.log("Sending XP to backend:", amount, "with token:", token);
    await fetch("https://matly-backend.onrender.com/api/user/add_xp/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ xp: amount }),
    });
  };

  // 🏅 Mark badge earned
  const markBadgeEarned = async () => {
    try {
      const res = await fetch("https://matly-backend.onrender.com/api/user/mark-badge-earned/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          game: "ligningsmester",
          level: 1,
        }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      console.log(data.message);
    } catch (err) {
      console.error("Failed to mark cleared", err);
    }
  };

  // ▶️ Handle next question
  const handleNext = async () => {
    // Always log the game and refresh progress after game ends
    if (!hasLoggedGame && questionNumber >= 10) {
      await logGame(); // 🧾 Log game progress
    }

    // Always fetch and update level progress after logging the game (if at game end or not)
    if (questionNumber >= 10) {
      const updatedProgress = await api.get("user/get_level_progress/"); // 🔄 Get updated level progress
      setLevelProgress(updatedProgress.data); // 📊 Store progress

      const clears = updatedProgress.data.find(
        (p) => p.game === "ligningsmester" && p.level === 1
      )?.clears || 0;

      if (clears > 2) {
        console.log("Calling markBadgeEarned");
        markBadgeEarned();
      }

      setShouldEndGame(true); // ⏳ Signal to end game after levelProgress updates
      return;
    }
    setQuestionNumber((prev) => prev + 1);
    getNewQuestion();
    setHasSubmitted(false);
    setHasOutreached(false);
  };

  // 🏁 check for level clearance
  useEffect(() => {
    if (score > 130 && !isClearedOnce) {
      setIsCleared(true);
      setIsClearedOnce(true);
    }
  }, [score, isClearedOnce]);

  // 🔢 Displayed question number (max 10)
  const displayNumber = questionNumber > 10 ? 10 : questionNumber;

  // 🚀 Start game on component mount
  useEffect(() => {
    startGame();
  }, [startGame]);

  // 🖼️ Render main UI
  return (
    <div className="pageGame">
      <Helmet>
        <title>ligningsmester: kapitel 1 | matly</title>
      </Helmet>
      <Header />

      {/* 📦 Main Content */}
      <div className="contentRow">
        {/* 🔝 Show end-of-game screen*/}
        {gameOver && (
          <ResultScreen
            isCleared={isCleared}
            levelProgress={levelProgress}
            score={score}
            startGame={startGame}
          />
        )}

        {/* 🎯 Equation Box */}
        <EquationBox
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          submitAnswer={submitAnswer}
          hasSubmitted={hasSubmitted}
          setHasSubmitted={setHasSubmitted}
          handleNext={handleNext}
          questionNumber={questionNumber}
        />

        {/* 📊 Sidebar */}
        <SideBar
          question={question}
          hasSubmitted={hasSubmitted}
          setHasSubmitted={setHasSubmitted}
          hasOutreached={hasOutreached}
          setHasOutreached={setHasOutreached}
          handleNext={handleNext}
          submitAnswer={submitAnswer}
          feedback={feedback}
          elapsedSeconds={elapsedSeconds}
          displayNumber={displayNumber}
          questionNumber={questionNumber}
        />
      </div>
    </div>
  );
}
